// Authenticated downloads without putting session tokens in URLs or browser history.
const PrivateDownloads = {
    _inlinePreviews: new WeakMap(),

    clearInlinePreview(container) {
        if (!container) return;
        const ownedUrl = this._inlinePreviews.get(container);
        container.replaceChildren();
        if (ownedUrl) URL.revokeObjectURL(ownedUrl);
        this._inlinePreviews.delete(container);
    },

    renderInlinePreview(container, value, fileType) {
        // Compatibility for old in-memory documents: never insert a URL into HTML.
        this.clearInlinePreview(container);
        let url, owned = false;
        const match = typeof value === 'string' && /^data:(application\/pdf|image\/(?:png|jpeg|gif|webp));base64,([A-Za-z0-9+/]*={0,2})$/.exec(value);
        if (match && match[2].length > 0 && match[2].length <= 28 * 1024 * 1024) {
            const decoded = atob(match[2]);
            // A declared PDF must really start with a PDF signature, not HTML.
            if (match[1] === 'application/pdf' && !decoded.startsWith('%PDF-')) throw new Error('PDF no válido');
            const bytes = Uint8Array.from(decoded, char => char.charCodeAt(0));
            url = URL.createObjectURL(new Blob([bytes], {type:match[1]}));
            owned = true;
            fileType = match[1];
        } else {
            const parsed = new URL(String(value || ''), window.location.href);
            if (parsed.protocol !== 'blob:' || parsed.origin !== window.location.origin) throw new Error('Vista previa no disponible: formato o dirección no permitidos');
            url = parsed.href;
        }
        const pdf = String(fileType || '').toLowerCase() === 'application/pdf';
        const node = document.createElement(pdf ? 'iframe' : 'img');
        node.src = url;
        node.title = 'Vista previa del documento';
        if (!pdf) node.alt = 'Vista previa del documento';
        node.style.cssText = pdf ? 'width:100%;height:100%;border:none;background:white;' : 'max-width:100%;max-height:100%;object-fit:contain;';
        try { container.replaceChildren(node); }
        catch (error) { if (owned) URL.revokeObjectURL(url); throw error; }
        if (owned) this._inlinePreviews.set(container, url);
        return url;
    },

    // Stored document (professor doc or justificante): a file chosen but not saved yet is still a
    // data URL; a saved one is fetched by id, so lists never carry file contents.
    storedUrl(filename, id) {
        return '/api/documents/file/' + filename + '/' + encodeURIComponent(id);
    },

    async renderStoredPreview(container, value, fileType, filename, id) {
        if (typeof value === 'string' && value.startsWith('data:')) return this.renderInlinePreview(container, value, fileType);
        const { blob } = await this.fetchFile(this.storedUrl(filename, id));
        const blobUrl = URL.createObjectURL(blob);
        try { this.renderInlinePreview(container, blobUrl, blob.type || fileType); }
        catch (error) { URL.revokeObjectURL(blobUrl); throw error; }
        this._inlinePreviews.set(container, blobUrl);
        return blobUrl;
    },

    isAllowed(url) {
        return url.origin === window.location.origin &&
            (/^\/uploads\/(?:signed-contracts|contract-templates|nominas|staff-contracts)\/[^/]+$/.test(url.pathname) ||
             url.pathname === '/api/student-contracts/zip' ||
             // Task material and student works: plain links cannot send the tab key (shared-device sessions).
             /^\/api\/tasks\/attachment\/[^/]+$/.test(url.pathname) ||
             /^\/api\/student-works\/[^/]+\/download\/[^/]+$/.test(url.pathname) ||
             // Professor documents and justificantes: the list carries no file, each one is fetched here.
             /^\/api\/documents\/file\/(?:professor-docs|justificantes)\.json\/[^/]+$/.test(url.pathname) ||
             /^\/api\/payroll\/(?:costs|list)\/export$/.test(url.pathname));
    },

    async fetchFile(value, {profile=false}={}) {
            const url = new URL(value, window.location.href);
            const profileFile=profile && url.origin===window.location.origin && /^\/uploads\/(?:alumnos|temp|avatars)\/[^/]+$/.test(url.pathname);
            if (!this.isAllowed(url) && !profileFile) throw new Error('Ruta de descarga no permitida');
            url.searchParams.delete('token');
            const token = AuthManager.getCsrfToken();
            if (!token) throw new Error('Inicia sesión para descargar el documento');
            const response = await fetch(url.href, {
                headers: { 'X-SICAF-CSRF': token }, cache: 'no-store', redirect: 'error'
            });
            if (!response.ok) throw new Error('Documento no disponible o sin permiso de acceso');
            const contentType = response.headers.get('content-type') || '';
            if (contentType.includes('text/html') || contentType.includes('application/json')) throw new Error('Respuesta de documento no válida');
            const disposition = response.headers.get('content-disposition') || '';
            const encodedName = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
            const plainName = /filename="([^"]+)"/i.exec(disposition)?.[1] || /filename=([^;"]+)/i.exec(disposition)?.[1];
            let filename = url.pathname.endsWith('/zip') ? 'Contratos.zip' : url.pathname.split('/').pop();
            try { filename = decodeURIComponent(encodedName || plainName || filename); } catch { filename = plainName || filename; }
            return { blob: await response.blob(), filename };
    },

    async preview(value, onReady) {
        let blobUrl;
        try {
            const { blob } = await this.fetchFile(value);
            blobUrl = URL.createObjectURL(blob);
            onReady(blobUrl);
            return true;
        } catch (error) {
            if (blobUrl) URL.revokeObjectURL(blobUrl);
            if (typeof Utils !== 'undefined') Utils.showToast(error.message, 'error');
            return false;
        }
    },

    async download(value, options) {
        try {
            const { blob, filename } = await this.fetchFile(value, options);
            const blobUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            anchor.download = filename.replace(/[\\/\x00-\x1f]/g, '_');
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
            return true;
        } catch (error) {
            if (typeof Utils !== 'undefined') Utils.showToast(error.message, 'error');
            return false;
        }
    }
};

document.addEventListener('click', event => {
    const anchor = event.target.closest?.('a[href]');
    if (!anchor) return;
    const url = new URL(anchor.href, window.location.href);
    if (!PrivateDownloads.isAllowed(url)) return;
    event.preventDefault();
    PrivateDownloads.download(url.href);
});
window.PrivateDownloads = PrivateDownloads;
